/**
 * Library Curator (Sheet-2 row 2, .claude/design/library-curator.md) — pure UI-side helpers.
 *
 * ISO/IEC 27001:2022 Cl. 7.5.3 (Documented information — control of changes / version
 * control) and A.5.37 (Documented operating procedures).
 *
 * The state machine mirrors libraryTemplates.controller.ts exactly:
 *   draft -> in_review (submit-review) -> approved (approve) -> published (publish)
 *   published -> retired (retire; reason + reasonType required)
 *   published | retired -> new draft version (new-version)
 *   PATCH only while draft / in_review.
 * Curation is platform_admin-only on the API; these predicates are UX affordances only.
 */
import { Role } from '../constants/roles';

export const LIBRARY_STATUSES = Object.freeze(['draft', 'in_review', 'approved', 'published', 'retired']);

export const LIBRARY_STATUS_LABELS = Object.freeze({
    draft: 'Draft',
    in_review: 'In review',
    approved: 'Approved',
    published: 'Published',
    retired: 'Retired',
});

export const LIBRARY_FAMILIES = Object.freeze(['Organizational', 'People', 'Physical', 'Technological']);

/** Reason types accepted by POST .../retire. "correction" additionally notifies every project
 * still citing the retired version (library-curator.md §3.1a). */
export const RETIRE_REASON_TYPES = Object.freeze([
    { value: 'routine', label: 'Routine - superseded by a newer version' },
    { value: 'correction', label: 'Correction - the template was factually wrong' },
]);

/** Only platform_admin curates the global library (design doc §4). */
export function canCurateLibrary(role) {
    return role === Role.ADMIN;
}

/** Project-scoped consumption read: the Stage-7 drafting personas. */
export function canLookUpLibraryTemplate(role) {
    return role === Role.LEAD_ADVISOR || role === Role.ADVISOR;
}

export function canEditItem(item) {
    return !!item && (item.status === 'draft' || item.status === 'in_review');
}

/** Which lifecycle actions are offered for an item in a given status. */
export function availableActions(item) {
    if (!item) return [];
    switch (item.status) {
        case 'draft':
            return ['edit', 'submit-review'];
        case 'in_review':
            return ['edit', 'approve'];
        case 'approved':
            return ['publish'];
        case 'published':
            return ['retire', 'new-version'];
        case 'retired':
            return ['new-version'];
        default:
            return [];
    }
}

/** AI-sourced items stay flagged "AI drafted — review" until a human `reviewedBy` exists. */
export function itemNeedsHumanReview(item) {
    return !!item && item.sourceType === 'ai_generated' && !item.reviewedBy;
}

export function parseTags(tagsJson) {
    if (!tagsJson) return [];
    try {
        const parsed = JSON.parse(tagsJson);
        return Array.isArray(parsed) ? parsed.filter((t) => typeof t === 'string') : [];
    } catch {
        return [];
    }
}

/** Splits a comma/newline separated tag box into a clean, de-duplicated tag array. */
export function splitTagInput(text) {
    const seen = new Set();
    const out = [];
    String(text || '')
        .split(/[,\n]/)
        .map((t) => t.trim())
        .filter(Boolean)
        .forEach((t) => {
            const key = t.toLowerCase();
            if (!seen.has(key)) {
                seen.add(key);
                out.push(t);
            }
        });
    return out;
}

/** Groups a flat item list by (standardId, controlRef) so version history reads together;
 * newest version first inside each group. */
export function groupByControl(items) {
    const groups = new Map();
    (items || []).forEach((item) => {
        const key = `${item.standardId}::${item.controlRef}`;
        if (!groups.has(key)) {
            groups.set(key, { key, standardId: item.standardId, controlRef: item.controlRef, items: [] });
        }
        groups.get(key).items.push(item);
    });
    return Array.from(groups.values()).map((g) => ({
        ...g,
        items: g.items.slice().sort((a, b) => b.version - a.version),
    }));
}

/** Turns the `GET /api/v1/standards` registry payload into Select options — the API validates
 * `standardId` against that same registry, so the picker is fed by it rather than a hard-coded list. */
export function standardOptionsFrom(standards) {
    return (Array.isArray(standards) ? standards : [])
        .filter((s) => s && typeof s.id === 'string')
        .map((s) => ({ value: s.id, label: s.displayName || s.id }));
}

/** ISO 27001/27002 control references are canonicalised server-side to "A.x.y"; other
 * standards keep their verbatim native citation (design doc §11.1/§11.2). */
export function controlRefPlaceholder(standardId) {
    return standardId === 'iso.27001-2022' || standardId === 'iso.27002-2022'
        ? 'A.5.15'
        : 'Verbatim citation, e.g. Req. 1.2.1';
}

/** Client-side required-field check for create; the server re-validates everything. */
export function validateCreateDraft(draft) {
    if (!draft?.standardId) return 'Choose a standard.';
    if (!String(draft.controlRef || '').trim()) return 'Control reference is required (for example A.5.15).';
    if (!String(draft.title || '').trim()) return 'Title is required.';
    return null;
}

/**
 * Parses the admin-only `sourceContextJson` provenance blob the AI draft/revise routes store
 * (grounding catalog + version, reasoningRefs, citationWarnings, changeSummary, instruction).
 * Returns null for a human-authored row or an unreadable blob — never a guessed default.
 * The consumption route strips this field, so a tenant-facing caller never has it.
 */
export function parseSourceContext(raw) {
    if (!raw || typeof raw !== 'string') return null;
    try {
        const p = JSON.parse(raw);
        if (!p || typeof p !== 'object') return null;
        const strings = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []);
        return {
            grounding: p.grounding && typeof p.grounding === 'object' ? p.grounding : null,
            reasoningRefs: strings(p.reasoningRefs),
            citationWarnings: strings(p.citationWarnings),
            changeSummary: typeof p.changeSummary === 'string' ? p.changeSummary : null,
            instruction: typeof p.instruction === 'string' ? p.instruction : null,
            confidenceMethod: typeof p.confidenceMethod === 'string' ? p.confidenceMethod : null,
            citationCheck: typeof p.citationCheck === 'string' ? p.citationCheck : null,
        };
    } catch {
        return null;
    }
}

/**
 * What the ONLY automated check on an AI library draft actually established. It is a
 * deterministic clause-reference TOKEN check against the Policy Hub master text — it says
 * nothing about whether the requirements in the body are correct. `not_applicable` (no
 * pattern exists for that standard) means NOTHING was checked and must never read as "0
 * warnings / all good"; `stale_after_human_edit` means a human changed the text afterwards
 * and the earlier result no longer applies. Returns null for a human row (no claim at all).
 */
export function citationCheckSummary(ctx) {
    if (!ctx) return null;
    const warnings = ctx.citationWarnings || [];
    switch (ctx.citationCheck) {
        case 'checked':
            return warnings.length > 0
                ? { kind: 'warnings', count: warnings.length, text: `${warnings.length} clause reference(s) in the draft were not found in the Policy Hub master text.` }
                : { kind: 'clean', count: 0, text: 'Reference check ran: every clause reference in the draft was found in the Policy Hub master text. This checks references only - it does not verify that the requirements are correct.' };
        case 'not_applicable':
            return { kind: 'not_applicable', count: 0, text: 'No automated reference check exists for this standard, so nothing was checked. Verify every reference yourself.' };
        case 'stale_after_human_edit':
            return { kind: 'stale', count: 0, text: 'A person edited this text after the AI draft, so the earlier reference check no longer applies and nothing was rechecked.' };
        default:
            return { kind: 'unknown', count: 0, text: 'No reference-check result is recorded for this draft.' };
    }
}

/** Coverage `templateStatus` values: a LibraryTemplateItem status, or 'none' for a gap. */
export function coverageStatusLabel(status) {
    return status === 'none' ? 'No template' : LIBRARY_STATUS_LABELS[status] || status;
}

export const GAP_ONLY_FILTER_LABEL = 'Show only controls without a published template';
